import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Users, Tree, CurrencyDollar, ChartBar, Shield,
  Key, Newspaper, Storefront, CreditCard, Coins, Percent, Gavel, BookOpen,
  Handshake, Article, FilePdf, ChatCircleDots, Palette, UserCircle,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import ContentEditor from "@/components/admin/ContentEditor";
import RegistryMonitor from "@/components/admin/RegistryMonitor";
import ApiManager from "@/components/admin/ApiManager";
import PaymentsManager from "@/components/admin/PaymentsManager";
import TokenManager from "@/components/admin/TokenManager";
import CommerceManager from "@/components/admin/CommerceManager";
import ModerationManager from "@/components/admin/ModerationManager";
import OverviewTab from "@/components/admin/tabs/OverviewTab";
import UsersTab from "@/components/admin/tabs/UsersTab";
import CarbonSubmissionsTab from "@/components/admin/tabs/CarbonSubmissionsTab";
import TreePostsTab from "@/components/admin/tabs/TreePostsTab";
import ApiKeysTab from "@/components/admin/tabs/ApiKeysTab";
import ApiReference from "@/components/admin/ApiReference";
import PartnersManager from "@/components/admin/PartnersManager";
import PagesManager from "@/components/admin/PagesManager";
import ResearchPapersManager from "@/components/admin/ResearchPapersManager";
import InboxManager from "@/components/admin/InboxManager";
import BrandingManager from "@/components/admin/BrandingManager";
import AdminProfileTab from "@/components/admin/tabs/AdminProfileTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_TABS = [
  { id: "overview", label: "Overview", Icon: ChartBar },
  { id: "moderation", label: "Moderation", Icon: Gavel },
  { id: "users", label: "Users", Icon: Users },
  { id: "registry", label: "Registry Monitor", Icon: Storefront },
  { id: "carbon", label: "Carbon Credits", Icon: CurrencyDollar },
  { id: "posts", label: "Tree Posts", Icon: Tree },
  { id: "api", label: "API Keys", Icon: Key },
  { id: "system_apis", label: "API Manager", Icon: Key },
  { id: "api_reference", label: "API Reference", Icon: BookOpen },
  { id: "payments", label: "Payments", Icon: CreditCard },
  { id: "commerce", label: "Commission", Icon: Percent },
  { id: "tokens", label: "TREE Tokens", Icon: Coins },
  { id: "partners", label: "Partners", Icon: Handshake },
  { id: "pages", label: "Site Pages", Icon: Article },
  { id: "papers", label: "Papers", Icon: FilePdf },
  { id: "inbox", label: "Inbox / Careers", Icon: ChatCircleDots },
  { id: "content", label: "Site Content", Icon: Newspaper },
  { id: "branding", label: "Site Branding", Icon: Palette },
  { id: "profile", label: "My Profile", Icon: UserCircle },
];

export default function AdminPage() {
  const { user } = useAuth();
  const initialTab = user?.role === "moderator" ? "moderation" : "overview";
  const [tab, setTab] = useState(initialTab);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [posts, setPosts] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [keyName, setKeyName] = useState("");
  const [newKey, setNewKey] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const [s, u, c, p, k] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/carbon/submissions`, { withCredentials: true }),
        axios.get(`${API}/admin/posts`, { withCredentials: true }),
        axios.get(`${API}/developer/keys`, { withCredentials: true }),
      ]);
      setStats(s.data);
      setUsers(u.data.users || []);
      setSubmissions(c.data.submissions || []);
      setPosts(p.data.posts || []);
      setApiKeys(k.data.keys || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (user?.role === "admin") fetchAll();
  }, [user?.role, fetchAll]);

  const handleVerify = async (id, action) => {
    await axios.post(`${API}/admin/carbon/${id}/verify`, { action }, { withCredentials: true });
    fetchAll();
  };

  const handleToggleUser = async (id) => {
    await axios.post(`${API}/admin/users/${id}/toggle`, {}, { withCredentials: true });
    fetchAll();
  };

  const handleGenKey = async () => {
    try {
      const res = await axios.post(`${API}/developer/keys`, { name: keyName || "Default" }, { withCredentials: true });
      setNewKey(res.data.api_key);
      setKeyName("");
      fetchAll();
    } catch { /* silent */ }
  };

  const handleRevokeKey = async (id) => {
    await axios.post(`${API}/developer/keys/${id}/revoke`, {}, { withCredentials: true });
    fetchAll();
  };

  if (!user || (user.role !== "admin" && user.role !== "moderator")) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <div className="text-center">
          <Shield size={48} className="text-red-400 mx-auto mb-4" />
          <p className="text-white font-heading text-lg">Admin / Moderator Access Required</p>
          <Link to="/dashboard" className="text-emerald-400 text-sm mt-2 block">Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  const isModeratorOnly = user.role === "moderator";
  const tabs = isModeratorOnly ? ALL_TABS.filter((t) => t.id === "moderation") : ALL_TABS;

  return (
    <div className="min-h-screen bg-stone-950 text-white" data-testid="admin-page">
      <header className="bg-stone-900/95 border-b border-stone-800 sticky top-0 z-50">
        <div className="h-12 sm:h-14 flex items-center px-3 sm:px-4 gap-3">
          <Link to="/dashboard" className="text-stone-400 hover:text-white"><ArrowLeft size={18} weight="bold" /></Link>
          <Shield size={18} weight="bold" className="text-amber-400" />
          <span className="font-heading text-sm font-semibold">Admin</span>
          <div className="flex-1" />
        </div>
        <div className="flex items-center gap-1.5 px-3 pb-2 overflow-x-auto scrollbar-thin">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors whitespace-nowrap shrink-0 ${tab === t.id ? "bg-emerald-600 text-white" : "text-stone-400 hover:text-white bg-stone-800/40"}`}
              data-testid={`admin-tab-${t.id}`}>
              <t.Icon size={14} weight="bold" />
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {tab === "moderation" && <ModerationManager />}
        {tab === "overview" && <OverviewTab stats={stats} />}
        {tab === "users" && <UsersTab users={users} onToggleUser={handleToggleUser} onRefresh={fetchAll} />}
        {tab === "carbon" && <CarbonSubmissionsTab submissions={submissions} onVerify={handleVerify} />}
        {tab === "posts" && <TreePostsTab posts={posts} />}
        {tab === "api" && (
          <ApiKeysTab apiKeys={apiKeys} keyName={keyName} setKeyName={setKeyName}
            newKey={newKey} onGenKey={handleGenKey} onRevokeKey={handleRevokeKey} />
        )}
        {tab === "content" && <ContentEditor />}
        {tab === "registry" && <RegistryMonitor />}
        {tab === "system_apis" && <ApiManager />}
        {tab === "api_reference" && <ApiReference />}
        {tab === "payments" && <PaymentsManager />}
        {tab === "commerce" && <CommerceManager />}
        {tab === "tokens" && <TokenManager />}
        {tab === "partners" && <PartnersManager />}
        {tab === "pages" && <PagesManager />}
        {tab === "papers" && <ResearchPapersManager />}
        {tab === "inbox" && <InboxManager />}
        {tab === "branding" && <BrandingManager />}
        {tab === "profile" && <AdminProfileTab />}
      </div>
    </div>
  );
}
